class SeparatedFilterPopup extends HTMLElement {
  connectedCallback() {
    this.closeDuration = 500;
    this.closingTimer = null;

    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  get isOpen() {
    return !this.hasAttribute('hidden');
  }

  onClick(event) {
    if (event.target === this || event.target.closest('[data-separated-filter-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  open() {
    clearTimeout(this.closingTimer);
    this.classList.remove('separated-filter--closing');
    this.removeAttribute('hidden');
    document.documentElement.classList.add('separated-filter--open');
    this.setTriggerState(true);
  }

  close() {
    if (!this.isOpen) return;

    clearTimeout(this.closingTimer);
    this.classList.add('separated-filter--closing');
    document.documentElement.classList.remove('separated-filter--open');
    this.setTriggerState(false);

    this.closingTimer = setTimeout(() => {
      this.setAttribute('hidden', '');
      this.classList.remove('separated-filter--closing');
    }, this.closeDuration);
  }

  setTriggerState(isOpen) {
    document
      .querySelectorAll('[data-new-filter-trigger="separated"]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', String(isOpen)));
  }
}

customElements.define('separated-filter-popup', SeparatedFilterPopup);

class NewFiltersBar extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onTriggerClick.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('change', this.onDrawerChange.bind(this));

    this.observeRenders();
  }

  get drawerDetails() {
    return document.querySelector('.mobile-facets__wrapper details');
  }

  get popup() {
    return document.querySelector('separated-filter-popup');
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-new-filter-trigger]');
    if (!trigger) return;

    event.preventDefault();

    if (trigger.dataset.newFilterTrigger === 'separated') {
      this.popup?.open();
      return;
    }

    const summary = this.drawerDetails?.querySelector('summary');
    if (!summary) return;

    summary.click();
    requestAnimationFrame(() => {
      trigger.setAttribute('aria-expanded', String(this.drawerDetails.hasAttribute('open')));
    });
  }

  onDocumentClick(event) {
    if (event.target.closest('.mobile-facets__chip')) {
      this.setDrawerLoading(true);
      return;
    }

    const details = this.drawerDetails;
    if (details?.hasAttribute('open') && event.target === details) {
      event.preventDefault();
      this.closeDrawer();
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (this.popup?.isOpen) return;
    if (this.drawerDetails?.hasAttribute('open')) this.closeDrawer();
  }

  onDrawerChange(event) {
    if (event.target.closest('.mobile-facets__wrapper .mobile-facets')) this.setDrawerLoading(true);
  }

  closeDrawer() {
    const details = this.drawerDetails;
    if (!details?.hasAttribute('open')) return;

    details.querySelector('summary')?.click();

    const observer = new MutationObserver(() => {
      if (details.hasAttribute('open')) return;
      observer.disconnect();
      document
        .querySelectorAll('[data-new-filter-trigger]')
        .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    });
    observer.observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  setDrawerLoading(isLoading) {
    const inner = document.querySelector('.mobile-facets__inner');
    if (!inner) return;

    inner.classList.toggle('mobile-facets--loading', isLoading);
    inner.setAttribute('aria-busy', String(isLoading));

    clearTimeout(this.loadingTimer);
    if (isLoading) this.loadingTimer = setTimeout(() => this.setDrawerLoading(false), 10000);
  }

  observeRenders() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    let pending = false;

    new MutationObserver(() => {
      if (pending) return;
      pending = true;

      requestAnimationFrame(async () => {
        pending = false;
        try {
          const response = await fetch(window.location.href);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          this.syncRegions(doc);
        } catch (error) {
          console.error('new-filters: failed to sync variant regions', error);
        } finally {
          this.setDrawerLoading(false);
        }
      });
    }).observe(grid, { childList: true });
  }

  syncRegions(doc) {
    this.swapRegion(doc, '.mobile-facets__heading-count', '.mobile-facets__heading:not(.small-hide)');
    this.swapRegion(doc, '.mobile-facets__active-chips', '.mobile-facets__footer-group', '.mobile-facets__footer');

    document.querySelectorAll('.product-count__text--new').forEach((el, index) => {
      const incoming = doc.querySelectorAll('.product-count__text--new')[index];
      if (incoming) el.innerHTML = incoming.innerHTML;
    });

    const trigger = document.querySelector('[data-new-filter-trigger="filters"]');
    const incomingTrigger = doc.querySelector('[data-new-filter-trigger="filters"]');
    if (trigger && incomingTrigger) {
      const currentBadge = trigger.querySelector('.collection__new-filter-count');
      const incomingBadge = incomingTrigger.querySelector('.collection__new-filter-count');

      if (incomingBadge && currentBadge) currentBadge.textContent = incomingBadge.textContent;
      else if (incomingBadge) trigger.appendChild(incomingBadge.cloneNode(true));
      else if (currentBadge) currentBadge.remove();
    }
  }

  swapRegion(doc, selector, parentSelector, before) {
    const incoming = doc.querySelector(selector);
    const current = document.querySelector(selector);

    if (incoming && current) {
      current.innerHTML = incoming.innerHTML;
      return;
    }

    if (!incoming && current) {
      current.remove();
      return;
    }

    if (incoming && parentSelector) {
      const parent = document.querySelector(parentSelector);
      if (parent) parent.insertBefore(incoming.cloneNode(true), before ? parent.querySelector(before) : null);
    }
  }
}

customElements.define('new-filters-bar', NewFiltersBar);
